import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Form, Button, Modal, Badge, Pagination } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Header from '../layouts/Header';
import OutletSelect from '../components/OutletSelect'; // adjust path as needed
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    PointElement,
    LineElement,
    LineController,
    Tooltip,
    Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import axiosInstance from '../api/axios';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    PointElement,
    LineElement,
    LineController,
    Tooltip,
    Legend
);

/* ─── Formatters ─── */
const safeNum = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;
const fmt = (val, dec = 2) =>
    `₹${safeNum(val).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtPct = (val, dec = 1) => `${safeNum(val).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}%`;
const compactCurrency = (val) => {
    const n = safeNum(val);
    if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}Cr`;
    if (Math.abs(n) >= 100000) return `₹${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}L`;
    if (Math.abs(n) >= 1000) return `₹${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: 1 })}K`;
    return fmt(n, 0);
};
const fmtNum = (val) => safeNum(val).toLocaleString('en-IN');
const fmtTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};
const fmtDateLabel = (df, cr) => {
    if (df === 'custom' && cr) return `${cr.start} → ${cr.end}`;
    return { today: 'Today', yesterday: 'Yesterday', last7Days: 'Last 7 Days', last30Days: 'Last 30 Days', thisMonth: 'This Month', lastMonth: 'Last Month' }[df] || df;
};

const getPeriodCopy = (dateFilter, appliedCustomRange) => {
    const label = fmtDateLabel(dateFilter, appliedCustomRange);
    const map = {
        today: {
            highlightsTitle: "Today's Highlights",
            heroTitle: 'Your Daily Snapshot is Ready',
            heroSub: 'Live snapshot for faster decisions',
            closureLabelSingular: 'Closure Today',
            closureLabelPlural: 'Closures Today',
        },
        yesterday: {
            highlightsTitle: "Yesterday's Highlights",
            heroTitle: "Yesterday's Snapshot is Ready",
            heroSub: 'Completed-day snapshot for faster review',
            closureLabelSingular: 'Closure Yesterday',
            closureLabelPlural: 'Closures Yesterday',
        },
        last7Days: {
            highlightsTitle: 'Last 7 Days Highlights',
            heroTitle: 'Your 7-Day Snapshot is Ready',
            heroSub: 'Recent performance snapshot for faster review',
            closureLabelSingular: 'Closure in Period',
            closureLabelPlural: 'Closures in Period',
        },
        last30Days: {
            highlightsTitle: 'Last 30 Days Highlights',
            heroTitle: 'Your 30-Day Snapshot is Ready',
            heroSub: 'Monthly trend snapshot for faster review',
            closureLabelSingular: 'Closure in Period',
            closureLabelPlural: 'Closures in Period',
        },
        thisMonth: {
            highlightsTitle: 'This Month Highlights',
            heroTitle: 'This Month Snapshot is Ready',
            heroSub: 'Month-to-date snapshot for faster decisions',
            closureLabelSingular: 'Closure This Month',
            closureLabelPlural: 'Closures This Month',
        },
        lastMonth: {
            highlightsTitle: 'Last Month Highlights',
            heroTitle: 'Last Month Snapshot is Ready',
            heroSub: 'Completed-month snapshot for faster review',
            closureLabelSingular: 'Closure Last Month',
            closureLabelPlural: 'Closures Last Month',
        },
        custom: {
            highlightsTitle: 'Custom Range Highlights',
            heroTitle: 'Your Custom Range Snapshot is Ready',
            heroSub: `Snapshot for ${label}`,
            closureLabelSingular: 'Closure in Range',
            closureLabelPlural: 'Closures in Range',
        },
    };
    return map[dateFilter] || map.today;
};
const fmtViewDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtViewTime = (d) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

/* ─── Date filter → { startDate, endDate } strings ─── */
const resolveDateRange = (dateFilter, appliedCustomRange) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
        case 'today': {
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
        case 'yesterday': {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            const s = toStr(y);
            return { startDate: s, endDate: s };
        }
        case 'last7Days': {
            const s = new Date(today);
            s.setDate(s.getDate() - 6);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'last30Days': {
            const s = new Date(today);
            s.setDate(s.getDate() - 29);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'thisMonth': {
            const s = new Date(today.getFullYear(), today.getMonth(), 1);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'lastMonth': {
            const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth(), 0);
            return { startDate: toStr(s), endDate: toStr(e) };
        }
        case 'custom': {
            if (appliedCustomRange) {
                return { startDate: appliedCustomRange.start, endDate: appliedCustomRange.end };
            }
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
        default: {
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
    }
};

const toLocalDateStr = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const inclusiveDayCount = (start, end) => {
    const diff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
    return diff + 1; // start date counts as Day 1
};

/* ─── Modal filter configs ─── */
const MODAL_CONFIGS = {
    total: { title: 'Total Orders', badge: 'primary', statusFilter: '', typeFilter: '' },
    cancelled: { title: 'Cancelled Orders', badge: 'danger', statusFilter: 'CANCELLED', typeFilter: '' },
    delayed: { title: 'Delayed Orders', badge: 'warning', statusFilter: '', typeFilter: '' },
    early: { title: 'Early Orders', badge: 'success', statusFilter: '', typeFilter: '' },
    complimentary: { title: 'Complimentary Orders', badge: 'secondary', statusFilter: '', typeFilter: 'COMPLIMENTARY' },
};


const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7Days' },
    { label: 'Last 30 Days', value: 'last30Days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'Custom Range', value: 'custom' },
];

// const getDefaultStartDate = () => {
//     const d = new Date();
//     d.setDate(d.getDate() - 91);
//     return d;
// };

const getDefaultStartDate = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
};

/* ─── Sub-components ─── */
const Card = ({ title, icon, children, style = {}, id }) => (
    <div id={id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'visible', ...style }}>
        {title && (
            <div style={{ padding: '9px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 7, background: '#fafafa' }}>
                <i className={`bi ${icon}`} style={{ fontSize: 11, color: '#4d8af0' }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.7px' }}>{title}</span>
            </div>
        )}
        <div style={{ padding: '11px 13px' }}>{children}</div>
    </div>
);

const ReeloEmptyState = ({ icon = 'bi-inbox', title = 'No data available', sub = 'Nothing to show for this period' }) => (
    <div style={{
        minHeight: 122,
        borderRadius: 16,
        border: '1px dashed #dbe5e7',
        background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        textAlign: 'center',
        padding: 18,
    }}>
        <i className={`bi ${icon}`} style={{ fontSize: 24, color: '#cbd5e1' }} />
        <div style={{ fontSize: 12, fontWeight: 950, color: '#64748b' }}>{title}</div>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#a6b2b5' }}>{sub}</div>
    </div>
);

const RankRow = ({ rank, name, qty, isTop }) => {
    const tone = isTop
        ? { color: '#10b981', bg: '#ecfdf5', soft: '#d1fae5', icon: rank === 1 ? 'bi-trophy-fill' : 'bi-arrow-up-right', badge: 'Fast mover' }
        : { color: '#f59e0b', bg: '#fffbeb', soft: '#fef3c7', icon: rank === 1 ? 'bi-arrow-down-circle-fill' : 'bi-arrow-down-right', badge: 'Needs push' };

    return (
        <div style={{
            background: rank === 1 ? tone.bg : '#fff',
            border: `1px solid ${rank === 1 ? tone.soft : '#edf2f7'}`,
            borderRadius: 14,
            padding: '10px 11px',
            marginBottom: 8,
            boxShadow: rank === 1 ? '0 10px 20px rgba(15,23,42,0.04)' : 'none',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 13,
                    background: rank === 1 ? tone.color : '#f1f5f9',
                    color: rank === 1 ? '#fff' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 950,
                    flexShrink: 0,
                }}>
                    {rank === 1 ? <i className={`bi ${tone.icon}`} /> : rank}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 950, color: '#102022', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {!name || name === 'N/A' ? <span style={{ color: '#cbd5e1' }}>—</span> : name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 900, color: tone.color, background: tone.bg, border: `1px solid ${tone.soft}`, padding: '2px 7px', borderRadius: 999 }}>
                            {rank === 1 ? tone.badge : `Rank #${rank}`}
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 950, color: tone.color, flexShrink: 0 }}>{fmtNum(qty)} sold</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CouponRow = ({ coupon, used, discount }) => {
    const avgDiscount = safeNum(used) > 0 ? safeNum(discount) / safeNum(used) : 0;
    return (
        <div style={{
            background: '#fff',
            border: '1px solid #ede9fe',
            borderRadius: 14,
            padding: '11px 12px',
            marginBottom: 8,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 13, background: '#f5f3ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="bi bi-tag-fill" style={{ fontSize: 13 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                        <span style={{ fontSize: 12.5, fontWeight: 950, color: '#2f2146', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{coupon || '—'}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 950, color: '#dc2626', whiteSpace: 'nowrap' }}>-{fmt(discount, 0)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 7, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10.5, fontWeight: 900, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ede9fe', padding: '2px 7px', borderRadius: 999 }}>{fmtNum(used)}x used</span>
                        <span style={{ fontSize: 10.5, fontWeight: 900, color: '#64748b', background: '#f8fafc', border: '1px solid #edf2f7', padding: '2px 7px', borderRadius: 999 }}>Avg {fmt(avgDiscount, 0)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReeloListCard = ({ icon, title, subtitle, metricLabel, metricValue, tone = '#10b981', children, footer }) => (
    <div style={{
        background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
        border: '1px solid #dfe7e8',
        borderRadius: 18,
        padding: 16,
        boxShadow: '0 12px 28px rgba(15,23,42,0.04)',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
    }}>
        <div style={{ position: 'absolute', right: -34, top: -38, width: 118, height: 118, borderRadius: 36, background: `${tone}13`, transform: 'rotate(-14deg)' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 13 }}>
            <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 13, background: `${tone}16`, color: tone, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={`bi ${icon}`} style={{ fontSize: 15 }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, lineHeight: 1.1, fontWeight: 950, color: '#102022' }}>{title}</div>
                        {subtitle && <div style={{ fontSize: 10.5, fontWeight: 750, color: '#7b8b8f', marginTop: 4 }}>{subtitle}</div>}
                    </div>
                </div>
            </div>
            {metricLabel && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 21, lineHeight: 1, fontWeight: 950, color: tone }}>{metricValue}</div>
                    <div style={{ fontSize: 9.5, fontWeight: 950, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.45px', marginTop: 5 }}>{metricLabel}</div>
                </div>
            )}
        </div>
        <div style={{ position: 'relative' }}>{children}</div>
        {footer && <div style={{ position: 'relative', marginTop: 11 }}>{footer}</div>}
    </div>
);

const CouponPerformanceCard = ({ coupons }) => {
    const list = coupons || [];
    const totalUsed = list.reduce((sum, c) => sum + safeNum(c.totalUsed), 0);
    const totalDiscount = list.reduce((sum, c) => sum + safeNum(c.totalDiscount), 0);
    return (
        <ReeloListCard
            icon="bi-ticket-perforated-fill"
            title="Coupon Performance"
            subtitle="Discount given through coupons"
            metricValue={fmt(totalDiscount, 0)}
            metricLabel="Discount"
            tone="#8b5cf6"
            footer={list.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <MiniMetricTile label="Total Used" value={`${fmtNum(totalUsed)}x`} color="#8b5cf6" bg="#f5f3ff" icon="bi-hand-index-thumb-fill" />
                    <MiniMetricTile label="Avg Discount" value={fmt(totalUsed > 0 ? totalDiscount / totalUsed : 0, 0)} color="#dc2626" bg="#fef2f2" icon="bi-percent" />
                </div>
            )}
        >
            {list.length > 0
                ? list.map((c) => <CouponRow key={c._id || c.couponCode} coupon={c.couponCode} used={c.totalUsed} discount={c.totalDiscount} />)
                : <ReeloEmptyState icon="bi-ticket-perforated" title="No coupon activity" sub="No coupons were used in this period" />
            }
        </ReeloListCard>
    );
};

const ProductPerformanceCard = ({ title, subtitle, products, isTop }) => {
    const list = products || [];
    const totalQty = list.reduce((sum, p) => sum + safeNum(p.totalQty), 0);
    const tone = isTop ? '#10b981' : '#f59e0b';
    return (
        <ReeloListCard
            icon={isTop ? 'bi-trophy-fill' : 'bi-arrow-down-circle-fill'}
            title={title}
            subtitle={subtitle}
            metricValue={fmtNum(totalQty)}
            metricLabel="Units"
            tone={tone}
            footer={list.length > 0 && (
                <div style={{ background: isTop ? '#ecfdf5' : '#fffbeb', color: tone, borderRadius: 12, padding: '9px 11px', fontSize: 11, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <i className={`bi ${isTop ? 'bi-stars' : 'bi-lightbulb-fill'}`} />
                    {isTop ? 'Best sellers driving the period' : 'Use this list for visibility, combos or staff push'}
                </div>
            )}
        >
            {list.length > 0
                ? list.map((p, i) => <RankRow key={p._id || p.productName || i} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={isTop} />)
                : <ReeloEmptyState icon={isTop ? 'bi-trophy' : 'bi-box'} title="No product data" sub="Product movement is unavailable for this period" />
            }
        </ReeloListCard>
    );
};

const ReeloShellCard = ({ children, style = {}, onClick }) => (
    <div
        onClick={onClick}
        style={{
            background: '#fff',
            border: '1px solid #dfe7e8',
            borderRadius: 18,
            boxShadow: '0 12px 28px rgba(15,23,42,0.04)',
            overflow: 'visible',
            position: 'relative',
            zIndex: 1,
            cursor: onClick ? 'pointer' : 'default',
            ...style,
        }}
    >
        {children}
    </div>
);

const ReeloSectionTitle = ({ icon, title, right }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className={`bi ${icon}`} style={{ fontSize: 21, color: '#667579' }} />
            <span style={{ fontSize: 19, fontWeight: 900, color: '#273337' }}>{title}</span>
        </div>
        {right}
    </div>
);

const ReeloHeroBanner = ({
    outletName,
    dateLabel,
    bestInsight,
    riskInsight,
    periodCopy
}) => (
    <div
        style={{
            borderRadius: 18,
            border: '1px solid rgba(255, 255, 255, 0.22)',
            background:
                'linear-gradient(135deg, #6D071A 0%, #A4133C 50%, #E63950 100%)',
            padding: '18px 22px',
            marginBottom: 22,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 14px 34px rgba(164, 19, 60, 0.22)',
        }}
    >
        {/* Decorative background shapes */}
        <div
            style={{
                position: 'absolute',
                right: 26,
                top: 17,
                width: 270,
                height: 125,
                borderRadius: 28,
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.08)',
                transform: 'rotate(-4deg)',
            }}
        />

        <div
            style={{
                position: 'absolute',
                right: -45,
                bottom: -65,
                width: 190,
                height: 190,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.07)',
            }}
        />

        <div
            style={{
                position: 'absolute',
                left: -60,
                top: -80,
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.045)',
            }}
        />

        {/* Main content */}
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 18,
                flexWrap: 'wrap',
                position: 'relative',
                zIndex: 2,
            }}
        >
            {/* Left section */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 18,
                    minWidth: 300,
                }}
            >
                {/* Icon */}
                <div
                    style={{
                        width: 88,
                        height: 88,
                        flexShrink: 0,
                        borderRadius: 24,
                        background:
                            'linear-gradient(145deg, #ffffff 0%, #ffe4e8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow:
                            '0 16px 30px rgba(70, 0, 20, 0.28)',
                        border: '1px solid rgba(255,255,255,0.45)',
                    }}
                >
                    <i
                        className="bi bi-speedometer2"
                        style={{
                            color: '#A4133C',
                            fontSize: 43,
                        }}
                    />
                </div>

                {/* Heading */}
                <div>
                    <div
                        style={{
                            fontSize: 28,
                            lineHeight: 1.05,
                            fontWeight: 900,
                            color: '#ffffff',
                            fontFamily: 'Georgia, serif',
                            letterSpacing: '-0.4px',
                        }}
                    >
                        {periodCopy?.heroTitle || 'Your Roundup is Ready'}
                    </div>

                    <div
                        style={{
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.78)',
                            marginTop: 8,
                            fontWeight: 500,
                        }}
                    >
                        {periodCopy?.heroSub || 'Snapshot for faster decisions'}
                    </div>

                    {/* Optional outlet/date information */}
                    {(outletName || dateLabel) && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 12,
                                flexWrap: 'wrap',
                            }}
                        >
                            {outletName && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '5px 10px',
                                        borderRadius: 8,
                                        background: 'rgba(255,255,255,0.13)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: '#fff',
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                >
                                    <i
                                        className="bi bi-shop"
                                        style={{ fontSize: 12 }}
                                    />
                                    {outletName}
                                </span>
                            )}

                            {dateLabel && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '5px 10px',
                                        borderRadius: 8,
                                        background: 'rgba(255,255,255,0.13)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: 'rgba(255,255,255,0.88)',
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                >
                                    <i
                                        className="bi bi-calendar3"
                                        style={{ fontSize: 11 }}
                                    />
                                    {dateLabel}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Quick overview card */}
            <div
                style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: 16,
                    padding: 13,
                    minWidth: 275,
                    boxShadow: '0 16px 32px rgba(70,0,20,0.22)',
                    border: '1px solid rgba(255,255,255,0.5)',
                }}
            >
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 900,
                        color: '#3A111B',
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                    }}
                >
                    <span
                        style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: '#C41E3A',
                            display: 'inline-block',
                        }}
                    />
                    Quick overview
                </div>

                {/* Best insight */}
                <div
                    style={{
                        background:
                            'linear-gradient(135deg, #FFF6D6 0%, #FFF1C2 100%)',
                        border: '1px solid #F3D98B',
                        borderRadius: 12,
                        padding: '9px 11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 8,
                    }}
                >
                    <span
                        style={{
                            width: 26,
                            height: 26,
                            flexShrink: 0,
                            borderRadius: 8,
                            background: '#FFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            fontSize: 14,
                        }}
                    >
                        👍
                    </span>

                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: '#493600',
                            lineHeight: 1.35,
                        }}
                    >
                        {bestInsight}
                    </span>
                </div>

                {/* Risk insight */}
                <div
                    style={{
                        background:
                            'linear-gradient(135deg, #FFF0F2 0%, #FFE4E8 100%)',
                        border: '1px solid #F4B8C1',
                        borderRadius: 12,
                        padding: '9px 11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <span
                        style={{
                            width: 26,
                            height: 26,
                            flexShrink: 0,
                            borderRadius: 8,
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            fontSize: 14,
                        }}
                    >
                        ⚠️
                    </span>

                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: '#651525',
                            lineHeight: 1.35,
                        }}
                    >
                        {riskInsight}
                    </span>
                </div>
            </div>
        </div>
    </div>
);

/* ── Custom-styled tooltip used throughout this screen (titles, metric
   labels, bottom stat cards). Rendered through a portal straight to
   document.body and positioned with `position: fixed` from the icon's
   real screen coordinates.
   This is deliberate: several usages sit inside ancestors with
   `overflow: hidden` (e.g. ReeloHighlightCard) or inside hover/pinned
   cards with their own stacking contexts (e.g. BottomStatCard). An
   absolutely-positioned tooltip confined to those ancestors gets clipped
   or fights z-index with sibling elements. Escaping to a portal sidesteps
   both problems entirely, regardless of which card the tooltip is used in. */
const InfoTip = ({ text }) => {
    const [show, setShow] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const iconRef = useRef(null);

    const updatePosition = useCallback(() => {
        const rect = iconRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({ top: rect.top, left: rect.left + rect.width / 2 });
    }, []);

    useEffect(() => {
        if (!show) return undefined;
        const handle = () => updatePosition();
        window.addEventListener('scroll', handle, true);
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, true);
            window.removeEventListener('resize', handle);
        };
    }, [show, updatePosition]);

    if (!text) return null;

    return (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <i
                ref={iconRef}
                className="bi bi-info-circle-fill"
                style={{ fontSize: 10, color: '#94a3b8', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.stopPropagation(); updatePosition(); setShow(true); }}
                onMouseLeave={(e) => { e.stopPropagation(); setShow(false); }}
                onClick={(e) => e.stopPropagation()}
            />
            {show &&
                ReactDOM.createPortal(
                    <div
                        role="tooltip"
                        style={{
                            position: 'fixed',
                            top: coords.top - 8,
                            left: coords.left,
                            transform: 'translate(-50%, -100%)',
                            background: '#102022',
                            color: '#fff',
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '6px 9px',
                            borderRadius: 8,
                            whiteSpace: 'normal',
                            width: 190,
                            lineHeight: 1.35,
                            textAlign: 'left',
                            boxShadow: '0 10px 24px rgba(15,23,42,0.3)',
                            zIndex: 1000000,
                            pointerEvents: 'none',
                        }}
                    >
                        {text}
                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: '5px solid #102022',
                            }}
                        />
                    </div>,
                    document.body
                )}
        </span>
    );
};

const ReeloHighlightCard = ({ title, value, icon, subtitle, tone = 'orange', onClick, tooltip }) => {
    const tones = {
        orange: { bg: '#fff6ed', color: '#e85d04', shape: '#ffd6a5' },
        pink: { bg: '#fff0f8', color: '#dd1f86', shape: '#ffc4e1' },
        blue: { bg: '#eef8ff', color: '#0875e1', shape: '#c2e4ff' },
        purple: { bg: '#f5f0ff', color: '#6537ff', shape: '#d9caff' },
        green: { bg: '#effcf4', color: '#0f9f6e', shape: '#c7f4d7' },
        red: { bg: '#fff1f1', color: '#dc2626', shape: '#fecaca' },
    };
    const t = tones[tone] || tones.orange;
    return (
        <div onClick={onClick} style={{ position: 'relative', overflow: 'hidden', minHeight: 104, background: t.bg, border: `1px solid ${t.shape}`, borderRadius: 16, padding: '16px 18px', cursor: onClick ? 'pointer' : 'default' }}>
            <div style={{ position: 'absolute', right: -15, bottom: -21, width: 102, height: 102, borderRadius: 26, background: `linear-gradient(135deg,${t.shape},rgba(255,255,255,0))`, transform: 'rotate(-13deg)' }} />
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: t.color, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {title}
                        {tooltip && <InfoTip text={tooltip} />}
                    </div>
                    <div style={{ fontSize: 25, lineHeight: 1, fontWeight: 900, color: '#122023' }}>{value}</div>
                    {subtitle && <div style={{ fontSize: 11, color: '#667579', fontWeight: 700, marginTop: 8 }}>{subtitle}</div>}
                </div>
                <div style={{ width: 48, height: 48, borderRadius: 16, background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`bi ${icon}`} style={{ fontSize: 24, color: t.color }} />
                </div>
            </div>
        </div>
    );
};

const ReeloBusinessMetric = ({ label, value, sub, active, onClick, tooltip }) => (
    <button type="button" onClick={onClick} style={{ border: 'none', background: 'transparent', textAlign: 'left', padding: '0 0 13px', borderBottom: active ? '4px solid #102022' : '4px solid transparent', minWidth: 135, cursor: onClick ? 'pointer' : 'default' }}>
        <div style={{ fontSize: 25, fontWeight: 900, color: active ? '#102022' : '#667579', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 13, fontWeight: 900, color: active ? '#102022' : '#667579', marginTop: 9, display: 'flex', alignItems: 'center', gap: 5 }}>
            {label}
            {tooltip && (
                <span onClick={(e) => e.stopPropagation()}>
                    <InfoTip text={tooltip} />
                </span>
            )}
        </div>
        {sub && <div style={{ fontSize: 10, fontWeight: 700, color: '#9aa8aa', marginTop: 3 }}>{sub}</div>}
    </button>
);

const ReeloAlertMetric = ({ value, sub, items }) => {
    const [hover, setHover] = useState(false);
    const activeItems = (items || []).filter((item) => safeNum(item.value) > 0);
    const allItems = items || [];

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ position: 'relative', display: 'inline-block', zIndex: hover ? 99999 : 2 }}
        >
            <ReeloBusinessMetric
                label="Alerts"
                value={value}
                sub={sub}
                active={false}
            />
            {hover && (
                <div style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 'calc(100% + 12px)',
                    transform: 'translateX(-50%)',
                    width: 255,
                    background: '#102022',
                    color: '#f8fafc',
                    borderRadius: 14,
                    padding: 12,
                    zIndex: 999999,
                    boxShadow: '0 18px 40px rgba(15,23,42,0.28)',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 7 }}>
                        <i className="bi bi-bell-fill" style={{ color: activeItems.length ? '#f87171' : '#34d399' }} />
                        Alert Breakdown
                    </div>
                    {allItems.map((item, idx) => (
                        <div key={item.label} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 10,
                            padding: '7px 0',
                            borderBottom: idx < allItems.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 700, color: '#dbe4e6' }}>
                                <i className={`bi ${item.icon}`} style={{ color: safeNum(item.value) > 0 ? item.color : '#64748b', fontSize: 12 }} />
                                {item.label}
                            </span>
                            <span style={{
                                minWidth: 28,
                                textAlign: 'center',
                                borderRadius: 999,
                                padding: '2px 8px',
                                fontSize: 11,
                                fontWeight: 900,
                                color: safeNum(item.value) > 0 ? item.color : '#94a3b8',
                                background: safeNum(item.value) > 0 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                            }}>
                                {fmtNum(item.value)}
                            </span>
                        </div>
                    ))}
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderTop: '7px solid #102022',
                    }} />
                </div>
            )}
        </div>
    );
};

const ReeloInsightPill = ({ icon, label, value, tone = 'green' }) => {
    const map = { green: ['#ecfdf5', '#047857'], amber: ['#fffbeb', '#b45309'], red: ['#fef2f2', '#b91c1c'], blue: ['#eff6ff', '#1d4ed8'], purple: ['#f5f3ff', '#6d28d9'] };
    const [bg, color] = map[tone] || map.green;
    return <div style={{ background: bg, color, borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 9 }}>
        <i className={`bi ${icon}`} style={{ fontSize: 15 }} />
        <div style={{ minWidth: 0 }}><div style={{ fontSize: 10, fontWeight: 800, opacity: 0.85 }}>{label}</div><div style={{ fontSize: 13, fontWeight: 900 }}>{value}</div></div>
    </div>;
};

const DonutRing = ({ pct, color, size = 78, stroke = 9 }) => {
    const r = (size - stroke) / 2;
    const c = size / 2;
    const circumference = 2 * Math.PI * r;
    const clamped = Math.max(0, Math.min(100, pct));
    const offset = circumference - (clamped / 100) * circumference;
    return (
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={c} cy={c} r={r} fill="none" stroke="#f1f3f5" strokeWidth={stroke} />
                <circle
                    cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={stroke}
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                    transform={`rotate(-90 ${c} ${c})`}
                    style={{ transition: 'stroke-dashoffset 0.4s ease' }}
                />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: '#111827' }}>
                {Math.round(clamped)}%
            </div>
        </div>
    );
};

const CompositionBars = ({ items, total, showAmount = true }) => {
    const activeItems = (items || []).filter(i => safeNum(i.value) > 0);
    const baseTotal = safeNum(total) > 0 ? safeNum(total) : activeItems.reduce((sum, i) => sum + safeNum(i.value), 0);

    if (!activeItems.length || baseTotal <= 0) {
        return <div style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', padding: '12px 0' }}>No data available</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeItems.map((item) => {
                const pct = baseTotal > 0 ? Math.min(100, (safeNum(item.value) / baseTotal) * 100) : 0;
                return (
                    <div key={item.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 5 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                                <span style={{ fontSize: 11.5, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexShrink: 0 }}>
                                {showAmount && <span style={{ fontSize: 11.5, fontWeight: 900, color: '#0f172a' }}>{fmt(item.value)}</span>}
                                <span style={{ fontSize: 10.5, fontWeight: 900, color: item.color }}>{fmtPct(pct, 0)}</span>
                            </div>
                        </div>
                        <div style={{ height: 8, background: '#edf2f7', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: item.color, transition: 'width 0.35s ease' }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const MiniMetricTile = ({ label, value, sub, color = '#0f172a', bg = '#f8fafc', icon }) => (
    <div style={{ background: bg, border: `1px solid ${color}18`, borderRadius: 14, padding: '12px 13px', minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <span style={{ fontSize: 10, fontWeight: 900, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.45px' }}>{label}</span>
            {icon && <i className={`bi ${icon}`} style={{ color, fontSize: 13 }} />}
        </div>
        <div style={{ fontSize: 18, lineHeight: 1, fontWeight: 950, color }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', marginTop: 6 }}>{sub}</div>}
    </div>
);

const RevenueChannelHealthCard = ({
    netSalesNum,
    zomatoRevenueNum,
    swiggyRevenueNum,
    netSalesWithAggregators,
    totalSalesNum,
    discountNum,
    complimentaryAmount,
    revenueHealthPct,
    onlineRevenueNum,
    onlineContribution,
    avgOrderValue,
    channelData,
    banner,
}) => {
    const channelTotal = (channelData || []).reduce((sum, c) => sum + safeNum(c.value), 0);
    const visibleChannels = (channelData || []).filter(c => safeNum(c.value) > 0);

    return (
        <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #dfe7e8', padding: 18, boxShadow: '0 12px 28px rgba(15,23,42,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 15 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 900, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.75px', marginBottom: 6 }}>Revenue + Channel Mix</div>
                    {/* <div style={{ fontSize: 27, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmt(netSalesNum)}</div> */}
                    <div style={{ fontSize: 27, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmt(netSalesWithAggregators)}</div>

                    {/* <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginTop: 7 }}>
                        Net sales from {fmt(totalSalesNum)} total sales
                    </div> */}
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginTop: 7 }}>
                        Net sales from {fmt(totalSalesNum + zomatoRevenueNum + swiggyRevenueNum, 0)} total sales
                        {(zomatoRevenueNum > 0 || swiggyRevenueNum > 0) && (
                            <> · incl. {fmt(zomatoRevenueNum + swiggyRevenueNum, 0)} from Zomato + Swiggy</>
                        )}
                    </div>
                </div>
                <div style={{ minWidth: 170, background: banner.bg, color: banner.color, fontSize: 11.5, fontWeight: 900, padding: '10px 12px', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <i className={`bi ${banner.icon}`} style={{ fontSize: 13 }} />{banner.text}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 9, marginBottom: 16 }}>
                <MiniMetricTile label="Revenue Health" value={fmtPct(revenueHealthPct, 0)} sub="Net / total sales" color="#10b981" bg="#ecfdf5" icon="bi-activity" />
                <MiniMetricTile label="Discount Burn" value={fmt(discountNum, 0)} sub={fmtPct(totalSalesNum > 0 ? (discountNum / totalSalesNum) * 100 : 0)} color="#f59e0b" bg="#fffbeb" icon="bi-percent" />
                <MiniMetricTile label="Online Share" value={fmtPct(onlineContribution, 0)} sub={fmt(onlineRevenueNum, 0)} color="#7c3aed" bg="#f5f3ff" icon="bi-phone-fill" />
                <MiniMetricTile label="AOV" value={compactCurrency(avgOrderValue)} sub="Avg order value" color="#0ea5e9" bg="#eff6ff" icon="bi-graph-up-arrow" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 950, color: '#102022', marginBottom: 10 }}>Revenue bifurcation</div>
                    <CompositionBars
                        total={Math.max(totalSalesNum, netSalesNum + discountNum + safeNum(complimentaryAmount))}
                        items={[
                            { label: 'Net Sales', value: netSalesNum, color: '#10b981' },
                            { label: 'Discount', value: discountNum, color: '#f59e0b' },
                            { label: 'Complimentary', value: complimentaryAmount, color: '#8b5cf6' },
                        ]}
                    />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 950, color: '#102022' }}>Sales by channel</div>
                        <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>{fmt(channelTotal, 0)}</div>
                    </div>
                    {visibleChannels.length ? (
                        <CompositionBars total={channelTotal} items={channelData} />
                    ) : (
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', padding: '12px 0' }}>No channel data available</div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ── Offline Orders / QR Web Orders performance card ──
   Backed by stats.quickServeInfo / stats.quickOrderInfo from
   getRevenueStatThroughSummaryByOutlet. Values here are plain
   numbers (not $numberDecimal wrapped). */
const QuickTypeMetricRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: '1px solid rgba(15,23,42,0.05)' }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: '#64748b' }}>{label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 950, color: color || '#102022' }}>{value}</span>
    </div>
);

/* ── Modern accordion primitive — collapsed by default, opens only on click ── */
const AccordionItem = ({ icon, iconColor, title, subtitle, badgeValue, badgeColor, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div
            style={{
                border: `1px solid ${open ? `${iconColor}33` : '#e6ecee'}`,
                borderRadius: 15,
                marginBottom: 10,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: open ? `0 10px 24px ${iconColor}14` : '0 2px 6px rgba(15,23,42,0.02)',
                transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            }}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '13px 15px',
                    background: open ? `${iconColor}08` : '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s ease',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                    <span style={{
                        width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                        background: `${iconColor}16`, color: iconColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className={`bi ${icon}`} style={{ fontSize: 15 }} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 950, color: '#102022', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                        {subtitle && <div style={{ fontSize: 10.5, fontWeight: 750, color: '#7b8b8f', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    {badgeValue != null && (
                        <span style={{ fontSize: 13, fontWeight: 950, color: iconColor, background: `${iconColor}12`, borderRadius: 999, padding: '5px 11px', whiteSpace: 'nowrap' }}>
                            {badgeValue}
                        </span>
                    )}
                    <i
                        className="bi bi-chevron-down"
                        style={{ fontSize: 12, color: '#94a3b8', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }}
                    />
                </div>
            </button>
            <div
                style={{
                    maxHeight: open ? 1200 : 0,
                    opacity: open ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.35s ease, opacity 0.25s ease',
                }}
            >
                <div style={{ padding: '2px 15px 15px' }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

/* ── Order Channels accordion — Offline (POS), QR Web/Website, App and Kiosk.
   All sections start collapsed; a section only opens when the user clicks it. ── */
const OrderChannelsAccordionCard = ({ quickServeInfo, quickOrderInfo, appRevenue, kioskRevenue }) => {
    const qs = quickServeInfo || {};
    const qo = quickOrderInfo || {};
    const appRev = safeNum(appRevenue);
    const kioskRev = safeNum(kioskRevenue);

    const offlineOrders = safeNum(qs.orderCountExclCancelled);
    const qrOrders = safeNum(qo.orderCountExclCancelled);
    const totalOrders = offlineOrders + qrOrders;
    const totalRevenue = safeNum(qs.orderRevenueExclCancelled) + safeNum(qo.orderRevenueExclCancelled) + appRev + kioskRev;

    // If nothing has any activity for the period, skip rendering the card entirely
    const hasActivity = safeNum(qs.orderCount) > 0 || safeNum(qo.orderCount) > 0 || appRev > 0 || kioskRev > 0;
    if (!hasActivity) return null;

    return (
        <ReeloShellCard style={{ padding: 18, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 900, color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '0.75px', marginBottom: 6 }}>
                        Offline Order + QR Web Orders
                    </div>
                    <div style={{ fontSize: 27, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmt(totalRevenue, 0)}</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginTop: 7 }}>
                        {fmtNum(totalOrders)} orders (excl. cancelled) across Offline &amp; QR Web · click a channel below to expand
                    </div>
                </div>
            </div>

            <AccordionItem
                icon="bi-lightning-charge-fill"
                iconColor="#b45309"
                title="Offline Orders"
                subtitle="POS counter sales"
                badgeValue={fmt(qs.orderRevenueExclCancelled, 0)}
            >
                <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 14, padding: '12px 14px' }}>
                    <QuickTypeMetricRow label="Orders (excl. cancelled)" value={`${fmtNum(qs.orderCountExclCancelled)} of ${fmtNum(qs.orderCount)}`} />
                    <QuickTypeMetricRow label="Revenue" value={fmt(qs.orderRevenueExclCancelled, 0)} color="#b45309" />
                    <QuickTypeMetricRow label="Taxable" value={fmt(qs.taxableExclCancelled, 0)} />
                    <QuickTypeMetricRow label="Tax" value={fmt(qs.taxAmountExclCancelled, 0)} />
                    <QuickTypeMetricRow label="Cancelled Orders" value={fmtNum(qs.cancelledOrderCount)} color={safeNum(qs.cancelledOrderCount) > 0 ? '#dc2626' : undefined} />
                </div>
            </AccordionItem>

            <AccordionItem
                icon="bi-receipt"
                iconColor="#0f766e"
                title="QR Web Orders (Website)"
                subtitle="Self-order via QR code / website"
                badgeValue={fmt(qo.orderRevenueExclCancelled, 0)}
            >
                <div style={{ background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: 14, padding: '12px 14px' }}>
                    <QuickTypeMetricRow label="Orders (excl. cancelled)" value={`${fmtNum(qo.orderCountExclCancelled)} of ${fmtNum(qo.orderCount)}`} />
                    <QuickTypeMetricRow label="Revenue" value={fmt(qo.orderRevenueExclCancelled, 0)} color="#0f766e" />
                    <QuickTypeMetricRow label="Taxable" value={fmt(qo.taxableExclCancelled, 0)} />
                    <QuickTypeMetricRow label="Tax" value={fmt(qo.taxAmountExclCancelled, 0)} />
                    <QuickTypeMetricRow label="Cancelled Orders" value={fmtNum(qo.cancelledOrderCount)} color={safeNum(qo.cancelledOrderCount) > 0 ? '#dc2626' : undefined} />
                </div>
            </AccordionItem>

            <AccordionItem
                icon="bi-phone-fill"
                iconColor="#378ADD"
                title="App Orders"
                subtitle="Orders placed through the mobile app"
                badgeValue={fmt(appRev, 0)}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 9 }}>
                    <MiniMetricTile label="Revenue" value={fmt(appRev, 0)} color="#378ADD" bg="#eff6ff" icon="bi-currency-rupee" />
                    <MiniMetricTile label="Share of Channel Mix" value={fmtPct(totalRevenue > 0 ? (appRev / totalRevenue) * 100 : 0, 0)} color="#378ADD" bg="#eff6ff" icon="bi-pie-chart-fill" />
                </div>
            </AccordionItem>

            <AccordionItem
                icon="bi-tablet-fill"
                iconColor="#1D9E75"
                title="Kiosk Orders"
                subtitle="Self-service kiosk sales"
                badgeValue={fmt(kioskRev, 0)}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 9 }}>
                    <MiniMetricTile label="Revenue" value={fmt(kioskRev, 0)} color="#1D9E75" bg="#ecfdf5" icon="bi-currency-rupee" />
                    <MiniMetricTile label="Share of Channel Mix" value={fmtPct(totalRevenue > 0 ? (kioskRev / totalRevenue) * 100 : 0, 0)} color="#1D9E75" bg="#ecfdf5" icon="bi-pie-chart-fill" />
                </div>
            </AccordionItem>
        </ReeloShellCard>
    );
};

/* ── Complimentary Orders summary card ──
   Surfaces stats.complimentaryOrders / complimentaryOrdersTaxable / complimentaryOrderAmount.
   Complimentary revenue is deliberately excluded from Net Sales / Total Sales,
   so this card carries an explicit note to avoid confusion when reconciling totals. */
const ComplimentaryOrdersCard = ({ complimentaryOrders, complimentaryTaxable, complimentaryAmount }) => {
    const count = safeNum(complimentaryOrders);
    if (count <= 0) return null;

    return (
        <ReeloShellCard style={{ padding: 18, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 15 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 900, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.75px', marginBottom: 6 }}>
                        Complimentary Orders
                    </div>
                    <div style={{ fontSize: 27, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmt(complimentaryAmount, 0)}</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginTop: 7 }}>
                        {fmtNum(count)} order{count > 1 ? 's' : ''} given complimentary
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 9, marginBottom: 13 }}>
                <MiniMetricTile label="Orders" value={fmtNum(count)} color="#8b5cf6" bg="#f5f3ff" icon="bi-gift-fill" />
                <MiniMetricTile label="Taxable" value={fmt(complimentaryTaxable, 0)} color="#0ea5e9" bg="#eff6ff" icon="bi-receipt" />
                {/* <MiniMetricTile label="Amount Given" value={fmt(complimentaryAmount, 0)} color="#dc2626" bg="#fef2f2" icon="bi-currency-rupee" /> */}
            </div>

            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '9px 11px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <i className="bi bi-info-circle-fill" style={{ fontSize: 13, color: '#c2410c', marginTop: 1, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: '#7c2d12', lineHeight: 1.4 }}>
                    Complimentary revenue & order count is not included in Net Sales or Total Sales or Total Orders.
                </span>
            </div>
        </ReeloShellCard>
    );
};

const OrderSegmentPill = ({ label, value, pct, color, icon, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            border: 'none',
            background: '#fff',
            padding: '9px 10px',
            borderRadius: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            cursor: onClick ? 'pointer' : 'default',
            boxShadow: '0 8px 18px rgba(15,23,42,0.04)',
            border: `1px solid ${color}22`,
            minWidth: 0,
            textAlign: 'left',
        }}
        title={onClick ? `View ${label}` : undefined}
    >
        <span style={{
            width: 31,
            height: 31,
            borderRadius: 11,
            background: `${color}16`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}>
            <i className={`bi ${icon}`} style={{ fontSize: 14 }} />
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 10, fontWeight: 950, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.35px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 3 }}>
                <strong style={{ fontSize: 16, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmtNum(value)}</strong>
                <small style={{ fontSize: 10.5, fontWeight: 900, color }}>{pct}</small>
            </span>
        </span>
    </button>
);

const OrderProgressStrip = ({ segments, total }) => {
    const activeSegments = (segments || []).filter((s) => safeNum(s.value) > 0);
    if (!activeSegments.length || safeNum(total) <= 0) {
        return <div style={{ height: 12, borderRadius: 999, background: '#eef2f7' }} />;
    }
    return (
        <div style={{ display: 'flex', width: '100%', height: 13, borderRadius: 999, overflow: 'hidden', background: '#eef2f7', boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.05)' }}>
            {activeSegments.map((s) => (
                <div
                    key={s.label}
                    title={`${s.label}: ${fmtNum(s.value)} (${s.pct})`}
                    style={{
                        width: `${Math.max(2, Math.min(100, (safeNum(s.value) / safeNum(total)) * 100))}%`,
                        background: s.color,
                        transition: 'width .3s ease',
                    }}
                />
            ))}
        </div>
    );
};

const OrderHealthControlCard = ({
    totalOrders, netOrders, earlyOrders, onTimeOrders, delayedOrders,
    cancelledOrders, complimentaryOrders, orderHealthPct, cancellationRate,
    delayedRate, earlyRate, banner, onOpenOrders,
}) => {
    const safeTotal = safeNum(totalOrders);
    const pctOfTotal = (n) => safeTotal > 0 ? `${Math.round((safeNum(n) / safeTotal) * 100)}%` : '0%';
    const completionRate = safeTotal > 0 ? (safeNum(netOrders) / safeTotal) * 100 : 0;

    const healthTone = orderHealthPct >= 80
        ? { color: '#10b981', bg: '#ecfdf5', label: 'Healthy Flow', icon: 'bi-check-circle-fill' }
        : orderHealthPct >= 55
            ? { color: '#f59e0b', bg: '#fffbeb', label: 'Watch Closely', icon: 'bi-exclamation-circle-fill' }
            : { color: '#ef4444', bg: '#fef2f2', label: 'Needs Action', icon: 'bi-exclamation-triangle-fill' };

    const segments = [
        { label: 'Early', value: earlyOrders, color: '#10b981', pct: pctOfTotal(earlyOrders) },
        { label: 'On Time', value: onTimeOrders, color: '#0ea5e9', pct: pctOfTotal(onTimeOrders) },
        { label: 'Delayed', value: delayedOrders, color: '#f59e0b', pct: pctOfTotal(delayedOrders) },
        { label: 'Cancelled', value: cancelledOrders, color: '#ef4444', pct: pctOfTotal(cancelledOrders) },
        { label: 'Complimentary', value: complimentaryOrders, color: '#8b5cf6', pct: pctOfTotal(complimentaryOrders) },
    ];

    const quickMetrics = [
        { label: 'Net Orders', value: netOrders, pct: `${fmtPct(completionRate, 0)} fulfilment`, color: '#102022', bg: '#f8fafc', icon: 'bi-bag-check-fill' },
        {
            label: 'Early', value: earlyOrders,
            // pct: fmtPct(earlyRate, 0), 
            color: '#10b981', bg: '#ecfdf5', icon: 'bi-lightning-charge-fill', onClick: () => onOpenOrders?.('early')
        },
        {
            label: 'Delayed', value: delayedOrders,
            // pct: fmtPct(delayedRate, 0), 
            color: '#f59e0b', bg: '#fffbeb', icon: 'bi-clock-history', onClick: () => onOpenOrders?.('delayed')
        },
        { label: 'Cancelled', value: cancelledOrders, pct: fmtPct(cancellationRate, 0), color: '#ef4444', bg: '#fef2f2', icon: 'bi-x-circle-fill', onClick: () => onOpenOrders?.('cancelled') },
    ];

    return (
        <div style={{
            background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
            borderRadius: 18, border: '1px solid #dfe7e8', padding: 18,
            boxShadow: '0 12px 28px rgba(15,23,42,0.04)', position: 'relative', overflow: 'hidden',
        }}>
            <div style={{ position: 'absolute', right: -38, top: -46, width: 140, height: 140, borderRadius: 44, background: `${healthTone.color}12`, transform: 'rotate(-14deg)' }} />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 15 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 950, color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '.75px', marginBottom: 6 }}>Orders Control Center</div>
                    <button type="button" onClick={() => onOpenOrders?.('total')} style={{ border: 'none', background: 'transparent', padding: 0, textAlign: 'left', cursor: 'pointer' }} title="View total orders">
                        <div style={{ fontSize: 33, lineHeight: 1, fontWeight: 950, color: '#102022' }}>{fmtNum(totalOrders)}</div>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#64748b', marginTop: 6 }}>
                            Total orders · {fmtNum(netOrders)} net orders
                            <i className="bi bi-box-arrow-up-right" style={{ fontSize: 9, marginLeft: 5, color: '#94a3b8' }} />
                        </div>
                    </button>
                </div>
                <div style={{ minWidth: 122, borderRadius: 16, background: healthTone.bg, border: `1px solid ${healthTone.color}24`, padding: '11px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 950, color: healthTone.color, textTransform: 'uppercase', letterSpacing: '.45px' }}>
                        <i className={`bi ${healthTone.icon}`} style={{ marginRight: 5 }} />{healthTone.label}
                    </div>
                    <div style={{ fontSize: 28, lineHeight: 1, fontWeight: 950, color: healthTone.color, marginTop: 8 }}>{fmtPct(orderHealthPct, 0)}</div>
                    <div style={{ fontSize: 10.5, fontWeight: 800, color: '#64748b', marginTop: 5 }}>order health</div>
                </div>
            </div>
            <div style={{ position: 'relative', marginBottom: 13 }}>
                <OrderProgressStrip segments={segments} total={safeTotal} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {segments.map((s) => (
                        <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 850, color: '#64748b' }}>
                            <span style={{ width: 8, height: 8, borderRadius: 99, background: s.color }} />
                            {s.label} <span style={{ color: s.color }}>{s.pct}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 9, marginBottom: 12 }}>
                {quickMetrics?.map((m) => (
                    <div key={m.label} onClick={m.onClick} style={{ background: m.bg, border: `1px solid ${m.color}18`, borderRadius: 14, padding: '10px 11px', cursor: m.onClick ? 'pointer' : 'default' }} title={m.onClick ? `View ${m.label} orders` : undefined}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 950, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.35px' }}>{m.label}</span>
                            <i className={`bi ${m.icon}`} style={{ fontSize: 13, color: m.color }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginTop: 7 }}>
                            <strong style={{ fontSize: 19, lineHeight: 1, fontWeight: 950, color: m.color }}>{fmtNum(m.value)}</strong>
                            <span style={{ fontSize: 10.5, fontWeight: 900, color: m.color }}>{m.pct}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const OpsStatusRow = ({ icon, label, count, color, onClick }) => (
    <div
        onClick={onClick}
        style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 4px', borderBottom: '1px solid #f9fafb',
            cursor: onClick ? 'pointer' : 'default', borderRadius: onClick ? 6 : 0, transition: 'background 0.12s',
        }}
        onMouseEnter={e => { if (onClick) e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={e => { if (onClick) e.currentTarget.style.background = ''; }}
        title={onClick ? `Click to view ${label}` : undefined}
    >
        <span style={{ fontSize: 11.5, color: '#374151', display: 'flex', alignItems: 'center', gap: 7 }}>
            <i className={`bi ${icon}`} style={{ fontSize: 12, color }} />{label}
            {onClick && <i className="bi bi-box-arrow-up-right" style={{ fontSize: 8, color: '#9ca3af' }} />}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: count > 0 ? color : '#9ca3af' }}>{fmtNum(count)}</span>
    </div>
);

const OpsHealthCard = ({ stockOuts, closures, damagedProducts, damagedRawMaterial, banner, onNavigate }) => {
    const allClear = stockOuts === 0 && closures === 0 && damagedProducts === 0 && damagedRawMaterial === 0;
    return (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Operations Health</div>
                {allClear && !banner && (
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bi bi-check-circle-fill" style={{ fontSize: 10 }} />All Clear
                    </span>
                )}
            </div>
            <OpsStatusRow icon="bi-exclamation-triangle-fill" label="Stock Outs" count={stockOuts} color="#dc2626" onClick={() => onNavigate?.('stockOut')} />
            <OpsStatusRow icon="bi-door-closed-fill" label="Outlet Closures" count={closures} color="#d97706" onClick={() => onNavigate?.('closures')} />
            <OpsStatusRow icon="bi-box-seam" label="Damaged Products" count={damagedProducts} color="#dc2626" onClick={() => onNavigate?.('damage')} />
            <OpsStatusRow icon="bi-boxes" label="Raw Material Damage" count={damagedRawMaterial} color="#dc2626" onClick={() => onNavigate?.('damage')} />
            {banner && (
                <div style={{ marginTop: 10, background: banner.bg, color: banner.color, fontSize: 11, fontWeight: 700, padding: '7px 10px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className={`bi ${banner.icon}`} style={{ fontSize: 11 }} />{banner.text}
                </div>
            )}
        </div>
    );
};

const HourlyComboChart = ({ hourlyDistribution }) => {
    const labels = (hourlyDistribution || []).map(h => h.timeSlot);
    const sales = (hourlyDistribution || []).map(h => Number(h.netSales) || 0);
    const orders = (hourlyDistribution || []).map(h => Number(h.totalOrders) || 0);

    const chartData = {
        labels,
        datasets: [
            {
                type: 'bar', label: 'Net Sales (₹)', data: sales,
                backgroundColor: (ctx) => { const chart = ctx.chart; const { ctx: c, chartArea } = chart; if (!chartArea) return '#85b7eb'; const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom); g.addColorStop(0, '#3b82f6'); g.addColorStop(1, '#8ec5ff'); return g; },
                borderRadius: 4, order: 2, yAxisID: 'y',
            },
            {
                type: 'line', label: 'Orders', data: orders,
                borderColor: '#df1f8f', backgroundColor: '#df1f8f', tension: 0.3,
                pointRadius: 4, pointBackgroundColor: '#df1f8f', order: 1, yAxisID: 'y1',
            },
        ],
    };

    const options = {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => ctx.dataset.label === 'Orders' ? `Orders: ${ctx.parsed.y}` : `Net Sales: ${fmt(ctx.parsed.y)}` } },
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, color: '#6b7280' } },
            y: { position: 'left', grid: { color: '#f3f4f6' }, ticks: { font: { size: 10 }, color: '#6b7280', callback: (v) => `₹${v}` } },
            y1: { position: 'right', grid: { drawOnChartArea: false }, ticks: { font: { size: 10 }, color: '#6b7280', precision: 0 } },
        },
    };

    if (!labels.length) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#d1d5db', fontSize: 12 }}>No hourly data</div>;

    return (
        <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 11 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280' }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: '#85b7eb', display: 'inline-block' }} />Net Sales
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280' }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: '#E50085', display: 'inline-block' }} />Orders
                </span>
            </div>
            <div style={{ position: 'relative', width: '100%', height: 230 }}>
                <Chart type="bar" data={chartData} options={options} />
            </div>
        </>
    );
};

const getStatusBadge = (status) => {
    const map = { COMPLETED: 'success', DELIVERED: 'primary', CONFIRMED: 'secondary', 'READY-TO-PICK': 'warning', CANCELLED: 'danger' };
    return map[status] || 'dark';
};

const OrdersModal = ({ show, onHide, modalType, outletId, dateFilter, appliedCustomRange, outletName }) => {
    const token = localStorage.getItem('authToken');
    const config = MODAL_CONFIGS[modalType] || MODAL_CONFIGS.total;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const LIMIT = 25;

    const fetchOrders = useCallback(async (page = 1) => {
        if (!outletId) return;
        try {
            setLoading(true);
            const { startDate, endDate } = resolveDateRange(dateFilter, appliedCustomRange);
            const params = { startDate, endDate, page: String(page), limit: String(LIMIT) };
            if (config.statusFilter) params.orderStatus = config.statusFilter;
            if (config.typeFilter) params.orderType = config.typeFilter;

            const res = await axiosInstance({
                method: 'POST', url: 'order/getOrdersForAdmin/new', params,
                data: { outletIds: [outletId] },
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            const d = res?.data?.data;
            if (d?.title === 'Success') {
                let fetched = d.orders || [];
                if (modalType === 'delayed') fetched = fetched.filter(o => o.orderArrivalStatus === 'DELAYED');
                if (modalType === 'early') fetched = fetched.filter(o => o.orderArrivalStatus === 'EARLY');
                const sorted = [...fetched].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setOrders(sorted);
                setTotalPages(d.paginationData?.totalPages || 1);
                setTotalCount(d.paginationData?.totalRecords || fetched.length);
                setCurrentPage(d.paginationData?.currentPage || page);
            } else {
                setOrders([]);
            }
        } catch (err) {
            console.error('Orders modal fetch error:', err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [outletId, dateFilter, appliedCustomRange, modalType, config, token]);

    useEffect(() => {
        if (show) { setCurrentPage(1); fetchOrders(1); } else { setOrders([]); }
    }, [show, fetchOrders]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        fetchOrders(page);
    };

    const { startDate, endDate } = resolveDateRange(dateFilter, appliedCustomRange);
    const dateLabel = startDate === endDate ? startDate : `${startDate} → ${endDate}`;

    const renderPages = () => {
        if (totalPages <= 1) return null;
        const items = [];
        const max = Math.min(totalPages, 7);
        let start = Math.max(1, currentPage - 3);
        let end = Math.min(totalPages, start + max - 1);
        if (end - start < max - 1) start = Math.max(1, end - max + 1);
        if (start > 1) { items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>); if (start > 2) items.push(<Pagination.Ellipsis key="e1" />); }
        for (let i = start; i <= end; i++) { items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>); }
        if (end < totalPages) { if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="e2" />); items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>); }
        return items;
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: config.badge === 'danger' ? '#fee2e2' : config.badge === 'warning' ? '#fef3c7' : config.badge === 'success' ? '#dcfce7' : config.badge === 'primary' ? '#eff6ff' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={config.badge === 'danger' ? 'bi bi-x-circle-fill' : config.badge === 'warning' ? 'bi bi-clock-history' : config.badge === 'success' ? 'bi bi-lightning-charge-fill' : config.badge === 'secondary' ? 'bi bi-gift-fill' : 'bi bi-bag-fill'} style={{ color: config.badge === 'danger' ? '#dc2626' : config.badge === 'warning' ? '#d97706' : config.badge === 'success' ? '#16a34a' : config.badge === 'primary' ? '#2563eb' : '#6b7280', fontSize: 16 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{config.title}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                            <i className="bi bi-shop" style={{ fontSize: 10, marginRight: 4 }} />{outletName}
                            <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
                            <i className="bi bi-calendar3" style={{ fontSize: 10, marginRight: 4 }} />{dateLabel}
                            {totalCount > 0 && (<><span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span><strong style={{ color: '#374151' }}>{fmtNum(totalCount)}</strong> records</>)}
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading orders…</span>
                    </div>
                ) : orders.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 10 }}>
                        <i className="bi bi-inbox" style={{ fontSize: 40, color: '#d1d5db' }} />
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>No orders found</div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>Try adjusting the date range</div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                    {['Sr No', 'Date / Time', 'Order No', 'Customer', 'Status', 'Taxable', 'Tax', 'Discount', 'Total'].map((h, i) => (
                                        <th key={h} style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap', textAlign: i >= 7 ? 'right' : 'left' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {orders?.map((order, idx) => {
                                    const isQuickOrder = order?.source === "QUICK-ORDER";
                                    const isQuickServe = order?.source === "QUICK-SERVE";
                                    const isQuickSource = isQuickOrder || isQuickServe;

                                    console.log("Order in early orders :", order);


                                    const name = isQuickSource
                                        ? "N/A"
                                        : order.user?.name ||
                                        order.outletUser?.name ||
                                        order.kioskUser?.name ||
                                        "N/A";

                                    const mobile = isQuickSource
                                        ? "N/A"
                                        : order.user?.mobile ||
                                        order.outletUser?.mobile ||
                                        order.kioskUser?.mobile ||
                                        "";

                                    const orderNo = `${order?.order_sequence || ""}${order.order_no || ""}`;

                                    return (
                                        <tr
                                            key={order._id || idx}
                                            style={{
                                                borderBottom: "1px solid #f0f2f5",
                                                transition: "background 0.1s",
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.background = "#f8fafc")
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.background = "")
                                            }
                                        >
                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    color: "#9ca3af",
                                                    fontSize: 12,
                                                }}
                                            >
                                                {(currentPage - 1) * LIMIT + idx + 1}
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 500,
                                                        color: "#1a202c",
                                                    }}
                                                >
                                                    {fmtViewDate(order.createdAt)}
                                                </div>

                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        color: "#9ca3af",
                                                    }}
                                                >
                                                    {fmtViewTime(order.createdAt)}
                                                </div>
                                            </td>

                                            <td style={{ padding: "10px 14px" }}>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        color: "#1a202c",
                                                        background: "#f0f2f5",
                                                        padding: "2px 7px",
                                                        borderRadius: 4,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {orderNo}
                                                </span>
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    minWidth: 140,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        color: "#1a202c",
                                                    }}
                                                >
                                                    {name}
                                                </div>

                                                {mobile && (
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            color: "#2563eb",
                                                        }}
                                                    >
                                                        {mobile}
                                                    </div>
                                                )}

                                                {order.kioskUser && !isQuickSource && (
                                                    <span style={pillStyle("#eff6ff", "#1d4ed8")}>
                                                        Kiosk
                                                    </span>
                                                )}

                                                {order.subscriptionId && (
                                                    <span style={pillStyle("#f0fdf4", "#15803d")}>
                                                        Subscription
                                                    </span>
                                                )}

                                                {(order.paymentMode === "COMPLIMENTARY" ||
                                                    order.grandTotal === 0) &&
                                                    !order.subscriptionId && (
                                                        <span style={pillStyle("#fdf4ff", "#7e22ce")}>
                                                            Complimentary
                                                        </span>
                                                    )}

                                                {order?.outletUser &&
                                                    order.source === "POS" &&
                                                    order.paymentMode !== "COMPLIMENTARY" &&
                                                    !order.subscriptionId && (
                                                        <span style={pillStyle("#fdf4ff", "#ce2295")}>
                                                            POS
                                                        </span>
                                                    )}
                                            </td>

                                            <td style={{ padding: "10px 14px" }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 4,
                                                    }}
                                                >
                                                    <Badge
                                                        bg={getStatusBadge(order.orderStatus)}
                                                        style={{
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {order.orderStatus}
                                                    </Badge>

                                                    {order.orderArrivalStatus === "DELAYED" && (
                                                        <span
                                                            style={{
                                                                ...arrivalStyle("#fef2f2", "#dc2626"),
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            ⏱ +{order.delayedArrival}m late
                                                        </span>
                                                    )}

                                                    {order.orderArrivalStatus === "EARLY" && (
                                                        <span
                                                            style={{
                                                                ...arrivalStyle("#f0fdf4", "#16a34a"),
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            ⚡ -{order.earlyArrival}m early
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 500,
                                                        color: "#059669",
                                                    }}
                                                >
                                                    ₹{order.totalTaxable?.toFixed(2)}
                                                </span>
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 500,
                                                        color: "#d97706",
                                                    }}
                                                >
                                                    ₹{order.totalTaxAmount?.toFixed(2)}
                                                </span>
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 500,
                                                        color: "#dc2626",
                                                    }}
                                                >
                                                    ₹{order.totalDiscount?.toFixed(2)}
                                                </span>
                                            </td>

                                            <td
                                                style={{
                                                    padding: "10px 14px",
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: "#2563eb",
                                                    }}
                                                >
                                                    ₹{order.grandTotal?.toFixed(2)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            {!loading && totalPages > 1 && (
                <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '10px 20px', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Page {currentPage} of {totalPages} · {fmtNum(totalCount)} total orders</span>
                    <Pagination className="mb-0" size="sm">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                        {renderPages()}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                    </Pagination>
                </Modal.Footer>
            )}
        </Modal>
    );
};

/* ─── Tiny style helpers ─── */
const pillStyle = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700,
    background: bg, color, marginTop: 3, border: `1px solid ${color}22`,
});
const arrivalStyle = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 6px', borderRadius: 12, fontWeight: 700, background: bg, color,
});

/* ─── Bottom summary card ─── */
const BottomStatCard = ({ id, icon, title, value, valueLabel, rows, severity, products, highlighted, footerNote, rawMaterials, toolTip }) => {
    const [hover, setHover] = useState(false);
    const [pinned, setPinned] = useState(false);
    const cardRef = React.useRef(null);
    const sevColors = {
        danger: { text: '#ef4444', bg: '#fef2f2', soft: '#fee2e2', chip: 'Action Needed', hint: 'Review immediately' },
        warning: { text: '#f59e0b', bg: '#fffbeb', soft: '#fef3c7', chip: 'Review', hint: 'Needs attention' },
        ok: { text: '#10b981', bg: '#ecfdf5', soft: '#d1fae5', chip: 'All Clear', hint: 'No issue in this period' },
    };
    const sc = sevColors[severity] || sevColors.ok;
    const hasProducts = products?.length > 0 || rawMaterials?.length > 0;
    const showWindow = hover || pinned;
    const isHighlighted = highlighted || pinned;

    // Keep the breakdown open once clicked, regardless of mouse movement.
    // Close it only when the user clicks somewhere outside this card.
    useEffect(() => {
        if (!pinned) return;
        const handleOutsideClick = (e) => {
            if (cardRef.current && !cardRef.current.contains(e.target)) {
                setPinned(false);
                setHover(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [pinned]);

    return (
        <div
            id={id}
            ref={cardRef}
            onMouseEnter={() => { if (!pinned) setHover(true); }}
            onMouseLeave={() => { if (!pinned) setHover(false); }}
            onClick={() => { if (hasProducts) setPinned(p => !p); }}
            style={{
                position: 'relative', background: '#fff', borderRadius: 18,
                border: isHighlighted ? `1px solid ${sc.text}` : '1px solid #dfe7e8',
                padding: 16, cursor: hasProducts ? 'pointer' : 'default',
                boxShadow: isHighlighted ? `0 0 0 5px ${sc.soft}, 0 14px 30px rgba(15,23,42,0.10)` : '0 12px 28px rgba(15,23,42,0.04)',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.18s ease',
                overflow: 'visible', minHeight: 178,
            }}
        >
            <div style={{ position: 'absolute', right: -14, top: -28, width: 96, height: 96, borderRadius: 32, background: `${sc.text}10`, transform: 'rotate(-14deg)' }} />

            <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 14, background: sc.bg, color: sc.text, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={`bi ${icon}`} style={{ fontSize: 16 }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, lineHeight: 1.15, fontWeight: 950, color: '#102022', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                        <div style={{ fontSize: 10.5, fontWeight: 850, color: '#7b8b8f', marginTop: 4 }}>{valueLabel}</div>
                    </div>
                </div>
                <span style={{ flexShrink: 0, borderRadius: 999, padding: '4px 8px', fontSize: 9.5, fontWeight: 950, color: sc.text, background: sc.bg, border: `1px solid ${sc.soft}` }}>
                    {sc.chip}
                </span>
            </div>

            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ fontSize: 31, lineHeight: 1, fontWeight: 950, color: sc.text }}>{value} {" "}
                        {toolTip && (
                            <InfoTip text={toolTip} />
                        )}
                    </div>
                    {hasProducts && (
                        <span style={{ fontSize: 10.5, fontWeight: 950, color: sc.text, background: sc.bg, border: `1px solid ${sc.soft}`, borderRadius: 999, padding: '3px 8px', whiteSpace: 'nowrap' }}>
                            View products
                        </span>
                    )}
                </div>
                {rows && rows.length > 0 && (
                    <div style={{ marginTop: 13, display: 'grid', gridTemplateColumns: rows.length > 2 ? '1fr' : 'repeat(1,minmax(0,1fr))', gap: 7 }}>
                        {rows.map((r, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 11.2, background: '#f8fafc', border: '1px solid #edf2f7', borderRadius: 10, padding: '7px 9px' }}>
                                <span style={{ color: '#64748b', fontWeight: 850, display: 'flex', alignItems: 'center', gap: 5 }}>
                                    {r.label}
                                    {r.tooltip && (
                                        <InfoTip text={r.tooltip} />
                                    )}
                                </span>
                                <span style={{ fontWeight: 950, color: r.color || '#102022' }}>{r.value}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ position: 'relative', marginTop: 12, fontSize: 10.5, fontWeight: 850, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={`bi ${hasProducts ? 'bi-hand-index-thumb' : severity === 'ok' ? 'bi-check-circle-fill' : 'bi-info-circle-fill'}`} style={{ color: sc.text }} />
                {footerNote || (hasProducts ? 'Hover/click to see product breakdown' : sc.hint)}
            </div>

            {showWindow && hasProducts && (
                <div style={{
                    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                    marginBottom: 10, background: '#102022', color: '#f9fafb', borderRadius: 14,
                    padding: '10px 5px',
                    minWidth: rawMaterials?.length > 0 ? 480 : 240,
                    maxWidth: rawMaterials?.length > 0 ? 1000 : 310,
                    boxShadow: '0 18px 40px rgba(15,23,42,0.32)',
                    zIndex: 999999, border: '1px solid rgba(255,255,255,0.08)',
                }}>
                    {/* Header */}
                    <div style={{ padding: '0 11px 8px', fontSize: 12, fontWeight: 950, color: '#fff', display: 'flex', alignItems: 'center', gap: 7 }}>
                        <i className={`bi ${icon}`} style={{ color: sc.text }} />
                        {rawMaterials?.length > 0 ? 'Damage Breakdown' : 'Product Breakdown'}
                    </div>

                    <div style={{ display: 'flex', gap: 0 }}>
                        {/* LEFT — Products */}
                        {products?.length > 0 && (
                            <div style={{ flex: 1, borderRight: rawMaterials?.length > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                {rawMaterials?.length > 0 && (
                                    <div style={{ padding: '4px 11px 6px', fontSize: 10, fontWeight: 950, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                        Products
                                    </div>
                                )}
                                <div style={{ maxHeight: 220, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                                    {(() => {
                                        const isGrouped = products.some(item => Array.isArray(item.products));

                                        if (isGrouped) {
                                            return products.map((category, ci) => {
                                                const categoryProducts = category.products || [];
                                                return (
                                                    <div key={category._id || ci}>
                                                        <div style={{ padding: '5px 11px', fontSize: 10, fontWeight: 950, color: sc.text, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(255,255,255,0.05)', borderTop: ci > 0 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                                                            {category.categoryName}
                                                        </div>
                                                        {categoryProducts.map((p, pi) => (
                                                            <div key={p.productId || pi} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 11px', fontSize: 11.5, fontWeight: 750, borderBottom: pi < categoryProducts.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {p.productName}
                                                                </span>
                                                                {(p.exhaustedCount != null || p.damagedCount != null || p.quantity != null) && (
                                                                    <span style={{ fontSize: 10.5, fontWeight: 950, color: sc.text, background: 'rgba(255,255,255,0.1)', borderRadius: 99, padding: '2px 8px', flexShrink: 0 }}>
                                                                        × {p.exhaustedCount ?? p.damagedCount ?? p.quantity}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            });
                                        }

                                        return products.map((p, pi) => (
                                            <div key={p.productId || pi} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 11px', fontSize: 11.5, fontWeight: 750, borderBottom: pi < products.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {p.productName}
                                                </span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* RIGHT — Raw Materials */}
                        {rawMaterials?.length > 0 && (
                            <div style={{ flex: 1 }}>
                                <div style={{ padding: '4px 11px 6px', fontSize: 10, fontWeight: 950, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                    Raw Materials
                                </div>
                                <div style={{ maxHeight: 220, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                                    {rawMaterials.map((rm, i) => (
                                        <div key={rm.rawMaterialId || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 11px', fontSize: 11.5, fontWeight: 750, borderBottom: i < rawMaterials.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {rm.rawMaterialName}
                                            </span>
                                            {rm.quantity != null && (
                                                <span style={{ fontSize: 10.5, fontWeight: 950, color: sc.text, background: 'rgba(255,255,255,0.1)', borderRadius: 99, padding: '2px 8px', flexShrink: 0 }}>
                                                    - {rm?.quantity} {rm?.unit}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Caret */}
                    <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '7px solid #102022' }} />
                </div>
            )}
        </div>
    );
};

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const OutletDetailScreen = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { outletId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('authToken');

    const {
        outletName = 'Outlet Details',
        dateFilter: rawDateFilter = 'today',
        customRange: initialCustomRange = null,
    } = location.state || {};

    const dateFilterMap = {
        last7days: 'last7Days',
        last30days: 'last30Days',
    };

    const initialDateFilter = dateFilterMap[rawDateFilter] || rawDateFilter;

    console.log("dateFilter:", initialDateFilter);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [dateFilter, setDateFilter] = useState(initialDateFilter);
    const [appliedCustomRange, setAppliedCustomRange] = useState(initialCustomRange);
    const [customStartDate, setCustomStartDate] = useState(
        initialCustomRange ? new Date(initialCustomRange.start) : getDefaultStartDate
    );
    const [isTableVisible, setIsTableVisible] = useState(true);

    const [customEndDate, setCustomEndDate] = useState(
        initialCustomRange ? new Date(initialCustomRange.end) : () => new Date()
    );
    const [customDateError, setCustomDateError] = useState('');

    /* ── Outlet selector state — pre-populated from navigation state ── */
    const [selectedOutlet, setSelectedOutlet] = useState(
        outletId && outletName
            ? { value: outletId, label: outletName, status: 'ACTIVE' }
            : null
    );

    /* Derived display name — strips "(City)" suffix if present */
    const activeOutletName = selectedOutlet?.label?.split(' (')[0] || outletName;
    const activeOutletId = selectedOutlet?.value || outletId;

    /* ── Orders modal state ── */
    const [ordersModal, setOrdersModal] = useState({ show: false, type: null });
    const openOrdersModal = (type) => setOrdersModal({ show: true, type });
    const closeOrdersModal = () => setOrdersModal({ show: false, type: null });

    /* ── Operational alert highlight state ── */
    const [highlightedAlert, setHighlightedAlert] = useState(null);
    const ALERT_CARD_IDS = {
        stockOut: 'alert-card-stock-out',
        closures: 'alert-card-closures',
        damage: 'alert-card-damage',
    };

    const handleOpsNavigate = (key) => {
        const targetId = ALERT_CARD_IDS[key];
        if (!targetId) return;
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedAlert(key);
        window.clearTimeout(handleOpsNavigate._t);
        handleOpsNavigate._t = window.setTimeout(() => setHighlightedAlert(null), 2200);
    };

    const fetchDetails = useCallback(async () => {
        if (!activeOutletId) return;
        try {
            setLoading(true); setError(null);
            let url = `/dashboard/getRevenueStatThroughSummaryByOutlet?outletId=${activeOutletId}`;
            url += dateFilter === 'custom' && appliedCustomRange
                ? `&filterType=custom&startDate=${appliedCustomRange.start}&endDate=${appliedCustomRange.end}`
                : `&filterType=${dateFilter}`;
            const res = await axiosInstance.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setData(res?.data?.data || null);
        } catch (err) {
            console.error(err);
            setError('Failed to load outlet details.');
        } finally {
            setLoading(false);
        }
    }, [activeOutletId, dateFilter, appliedCustomRange, token]);

    const formatMinutes = (mins = 0) => {
        const hrs = Math.floor(mins / 60);
        const rem = mins % 60;
        if (hrs === 0) return `${rem} Min`;
        if (rem === 0) return `${hrs} Hr`;
        return `${hrs} Hr ${rem} Min`;
    };

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const handleDateFilterChange = (val) => {
        setDateFilter(val);
        setCustomDateError('');
        if (val === 'custom') {
            const start = getDefaultStartDate();
            const end = new Date();
            setCustomStartDate(start);
            setIsTableVisible(false);
            setCustomEndDate(end);
            setAppliedCustomRange({ start: toLocalDateStr(start), end: toLocalDateStr(end) });
        } else {
            setAppliedCustomRange(null);
            setCustomStartDate(null);
            setCustomEndDate(null);
            setIsTableVisible(true);
        }
    };

    const handleApplyCustomRange = () => {
        if (!customStartDate || !customEndDate) { setCustomDateError('Please select both dates.'); return; }
        const diffDays = inclusiveDayCount(customStartDate, customEndDate);
        if (diffDays > 90) { setCustomDateError('Range cannot exceed 90 days.'); return; }
        setCustomDateError('');
        setAppliedCustomRange({ start: toLocalDateStr(customStartDate), end: toLocalDateStr(customEndDate) });
    };

    useEffect(() => {
        const s = document.createElement('style');
        s.id = 'outlet-print-style';
        s.innerHTML = `@media print { body > *:not(#outlet-detail-root) { display:none!important; } #outlet-detail-root { display:block!important; background:#fff!important; } .no-print { display:none!important; } @page { margin:15mm; } }`;
        document.head.appendChild(s);
        return () => document.getElementById('outlet-print-style')?.remove();
    }, []);

    const { stats, hourlyDistribution, topProducts, bottomProducts,
        topCoupons, totalOnlineSales, outletShiftSummary,
        pettyCashSummary, damagedProductsSummary, exhaustedStockSummary,
        userStatsSummary, rawMaterialSummary } = data || {};

    const netOrders = (Number(stats?.totalOrders) || 0) - (Number(stats?.cancelledOrders) || 0);
    const periodCopy = getPeriodCopy(dateFilter, appliedCustomRange);

    const totalSalesNum = Number(stats?.totalSales) || 0;
    const netSalesNum = Number(stats?.netSales) || 0;
    const discountNum = Number(stats?.discountAmount) || 0;
    const revenueHealthPct = totalSalesNum > 0 ? (netSalesNum / totalSalesNum) * 100 : 0;
    const revenueBanner = revenueHealthPct >= 85
        ? { bg: '#dcfce7', color: '#16a34a', icon: 'bi-graph-up-arrow', text: 'Good Revenue Performance' }
        : revenueHealthPct >= 65
            ? { bg: '#fef3c7', color: '#d97706', icon: 'bi-graph-up', text: 'Fair Revenue Performance' }
            : { bg: '#fee2e2', color: '#dc2626', icon: 'bi-exclamation-triangle', text: 'Needs Attention' };

    const totalOrdersNum = Number(stats?.totalOrders) || 0;
    const earlyNum = Number(stats?.earlyOrders) || 0;
    const delayedNum = Number(stats?.delayedOrders) || 0;
    const cancelledNum = Number(stats?.cancelledOrders) || 0;
    const complimentaryNum = Number(stats?.complimentaryOrders) || 0;

    const zomatoRevenueNum = Number(totalOnlineSales?.zomato?.revenue) || 0;
    const swiggyRevenueNum = Number(totalOnlineSales?.swiggy?.revenue) || 0;
    const netSalesWithAggregators = netSalesNum + zomatoRevenueNum + swiggyRevenueNum;

    // ── Offline Orders / QR Web Orders breakdown from stats.quickServeInfo / stats.quickOrderInfo
    const quickServeInfo = stats?.quickServeInfo || {};
    const quickOrderInfo = stats?.quickOrderInfo || {};

    const onTimeNum = Math.max(totalOrdersNum - earlyNum - delayedNum - cancelledNum - complimentaryNum, 0);
    const orderHealthPct = totalOrdersNum > 0 ? ((earlyNum + onTimeNum) / totalOrdersNum) * 100 : 0;
    const orderBanner = orderHealthPct >= 80
        ? { bg: '#dcfce7', color: '#16a34a', icon: 'bi-graph-up-arrow', text: 'Good Order Performance' }
        : orderHealthPct >= 55
            ? { bg: '#fef3c7', color: '#d97706', icon: 'bi-graph-up', text: 'Fair Order Performance' }
            : { bg: '#fee2e2', color: '#dc2626', icon: 'bi-exclamation-triangle', text: 'Needs Attention' };

    const stockOutsNum = Number(exhaustedStockSummary?.totalExhaustedEntries) || 0;
    const closuresNum = Number(outletShiftSummary?.outletClosedTotalTimes) || 0;
    const damagedProductsNum = Number(damagedProductsSummary?.totalDamagedProducts) || 0;
    const damagedRawMaterialNum = Number(data?.rawMaterialSummary?.totalTransactions) || 0;
    const opsIssueCount = (stockOutsNum > 0 ? 1 : 0) + (closuresNum > 0 ? 1 : 0) + (damagedProductsNum > 0 ? 1 : 0) + (damagedRawMaterialNum > 0 ? 1 : 0);
    const opsBanner = opsIssueCount === 0
        ? { bg: '#dcfce7', color: '#16a34a', icon: 'bi-check-circle', text: 'All Clear' }
        : opsIssueCount <= 1
            ? { bg: '#fef3c7', color: '#d97706', icon: 'bi-exclamation-triangle', text: 'Minor Issues' }
            : { bg: '#fee2e2', color: '#dc2626', icon: 'bi-exclamation-triangle-fill', text: 'Needs Attention' };

    // stats.appNetSale bundles in Offline Order + QR Web Orders revenue too, so once
    // those are broken out as their own channel rows below, subtract them here —
    // otherwise the same rupees get counted twice across the composition bars.
    const quickServeRevenueNum = safeNum(quickServeInfo?.orderRevenueExclCancelled);
    const quickOrderRevenueNum = safeNum(quickOrderInfo?.orderRevenueExclCancelled);
    const appOnlyNetSale = Math.max((Number(stats?.appNetSale) || 0) - quickServeRevenueNum - quickOrderRevenueNum, 0);
    const kioskNetSaleNum = Number(stats?.kioskNetSale) || 0;

    const channelData = [
        { label: 'App', value: appOnlyNetSale, color: '#378ADD' },
        { label: 'Zomato', value: Number(totalOnlineSales?.zomato?.revenue) || 0, color: '#cf3a52' },
        { label: 'Swiggy', value: Number(totalOnlineSales?.swiggy?.revenue) || 0, color: '#D85A30' },
        { label: 'Kiosk', value: kioskNetSaleNum, color: '#1D9E75' },
        // ── Offline Orders (POS) and QR Web Orders (QR/mobile) as separate channels
        { label: 'Offline Orders', value: quickServeRevenueNum, color: '#b45309' },
        { label: 'QR Web Order', value: quickOrderRevenueNum, color: '#0f766e' },
    ];

    const avgOrderValue = netOrders > 0 ? netSalesNum / netOrders : 0;
    const cancellationRate = totalOrdersNum > 0 ? (cancelledNum / totalOrdersNum) * 100 : 0;
    const delayedRate = totalOrdersNum > 0 ? (delayedNum / totalOrdersNum) * 100 : 0;
    const earlyRate = totalOrdersNum > 0 ? (earlyNum / totalOrdersNum) * 100 : 0;
    const discountRate = totalSalesNum > 0 ? (discountNum / totalSalesNum) * 100 : 0;
    const onlineRevenueNum = safeNum(totalOnlineSales?.totalRevenue);
    const onlineContribution = totalSalesNum > 0 ? (onlineRevenueNum / totalSalesNum) * 100 : 0;
    const issueTotal = stockOutsNum + closuresNum + damagedProductsNum + damagedRawMaterialNum;
    const bestHour = (hourlyDistribution || []).reduce((best, h) => safeNum(h.netSales) > safeNum(best?.netSales) ? h : best, null);
    const bestInsight = bestHour?.timeSlot ? `${bestHour.timeSlot} is your best slot with ${compactCurrency(bestHour.netSales)}` : `${fmtPct(orderHealthPct, 0)} order health score`;
    const riskInsight = issueTotal > 0 ? `${fmtNum(issueTotal)} operational issue${issueTotal > 1 ? 's' : ''} need attention` : `No major operational alerts`;

    const businessMetrics = [
        {
            label: 'Total Sales', value: compactCurrency(totalSalesNum),
            // sub: `${fmtPct(revenueHealthPct)} revenue health`,
            active: true,
            tooltip: 'This does not include Zomato & Swiggy sales',
        },
        { label: 'Total Orders', value: fmtNum(totalOrdersNum), sub: `${fmtNum(netOrders)} net orders`, onClick: () => openOrdersModal('total') },
        { label: 'AOV', value: compactCurrency(avgOrderValue), sub: 'Average order value' },
    ];
    const alertBreakdownItems = [
        { label: 'Stock Outs', value: stockOutsNum, icon: 'bi-exclamation-triangle-fill', color: '#f87171' },
        { label: 'Outlet Closures', value: closuresNum, icon: 'bi-door-closed-fill', color: '#fbbf24' },
        { label: 'Damaged Products', value: damagedProductsNum, icon: 'bi-box-seam', color: '#fb7185' },
        { label: 'Raw Material Damage', value: damagedRawMaterialNum, icon: 'bi-boxes', color: '#fb7185' },
    ];

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div id="outlet-detail-root" className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* ── Top Bar ── */}
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    {/* Back button */}
                    <button
                        onClick={() => navigate(-1)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                        <i className="bi bi-arrow-left" /> Back
                    </button>

                    {/* Controls */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">

                        {/* ── Outlet Selector ── */}
                        <div style={{ minWidth: 260 }}>
                            <OutletSelect
                                showLabel={false}
                                placeholder="Select outlet..."
                                isClearable={false}
                                value={selectedOutlet}
                                onChange={(opt) => {
                                    if (opt) setSelectedOutlet(opt);
                                }}
                                extraStyles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        borderColor: state.isFocused ? '#86b7fe' : 'rgb(77, 138, 240)',
                                        minHeight: '38px',
                                        background: '#f8f9fa',
                                        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                                        '&:hover': { borderColor: '#4d8af0' },
                                    }),
                                }}
                            />
                        </div>

                        {/* ── Date Filter ── */}
                        <div className="d-flex align-items-center bg-light p-1 ps-2 rounded " style={{ gap: 8, border: '0.5px solid rgb(77, 138, 240)' }}>
                            <i className="bi bi-calendar-event text-primary" style={{ fontSize: 12 }} />
                            <div>
                                <Form.Select
                                    value={dateFilter}
                                    onChange={(e) => handleDateFilterChange(e.target.value)}
                                    size="sm"
                                    style={{ width: 145 }}
                                    className="border-0 bg-light"
                                >
                                    {DATE_FILTER_OPTIONS?.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                                {["last7Days", "last30Days", "thisMonth", "lastMonth"].includes(dateFilter) && (
                                    <span style={{ fontSize: 11, color: "#94a3b8", width: "100%" }}>
                                        * Today's data not included
                                    </span>
                                )}
                                {/* {['last7Days', 'last30Days', 'thisMonth', 'lastMonth'].includes(dateFilter) && (
                                    <span style={{ fontSize: 10, color: '#888', width: '100%', paddingLeft: 2 }}>
                                        * Showing data for the selected period
                                    </span>
                                )} */}
                            </div>
                            {dateFilter === 'custom' && (
                                <div className="d-flex align-items-center gap-2">
                                    <div className="d-flex flex-column">
                                        <span style={{ fontSize: 10, color: '#262626', marginBottom: 2 }}>Start date</span>
                                        {/* <DatePicker
                                            selected={customStartDate}
                                            onChange={(date) => {
                                                setCustomStartDate(date);
                                                if (customEndDate) {
                                                    const diff = Math.ceil((customEndDate - date) / (1000 * 60 * 60 * 24));
                                                    if (diff > 92 || diff < 0) setCustomEndDate(null);
                                                }
                                                setCustomDateError("");
                                            }}
                                            selectsStart
                                            startDate={customStartDate}
                                            endDate={customEndDate}
                                            minDate={new Date(new Date().setDate(new Date().getDate() - 92))}
                                            maxDate={new Date()}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="Start date"
                                            className="form-control form-control-sm reelo-date-input"
                                            wrapperClassName="date-picker-wrapper"
                                        /> */}
                                        <DatePicker
                                            selected={customStartDate}
                                            onChange={(date) => {
                                                setCustomStartDate(date);
                                                if (date) {
                                                    const today = new Date();
                                                    const maxAllowedEnd = new Date(date);
                                                    maxAllowedEnd.setDate(maxAllowedEnd.getDate() + 89); // start date = Day 1, so +89 more days = Day 90
                                                    const suggestedEnd = maxAllowedEnd > today ? today : maxAllowedEnd;
                                                    if (!customEndDate) {
                                                        setCustomEndDate(suggestedEnd);
                                                    } else {
                                                        const diff = inclusiveDayCount(date, customEndDate);
                                                        if (diff > 90 || diff < 1) {
                                                            setCustomEndDate(suggestedEnd);
                                                        }
                                                    }
                                                }
                                                setCustomDateError("");
                                            }}
                                            selectsStart
                                            startDate={customStartDate}
                                            endDate={customEndDate}
                                            maxDate={new Date()}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="Start date"
                                            className="form-control form-control-sm reelo-date-input"
                                            wrapperClassName="date-picker-wrapper"
                                            style={{ zIndex: 9999 }}
                                        />
                                    </div>
                                    <span style={{ fontSize: 13, color: '#888', marginTop: 14 }}>→</span>
                                    <div className="d-flex flex-column">
                                        <span style={{ fontSize: 10, color: '#262626', marginBottom: 2 }}>End date</span>
                                        <DatePicker
                                            selected={customEndDate}
                                            onChange={(date) => {
                                                setCustomEndDate(date);
                                                setCustomDateError("");
                                            }}
                                            selectsEnd
                                            startDate={customStartDate}
                                            endDate={customEndDate}
                                            minDate={customStartDate || new Date(new Date().setDate(new Date().getDate() - 90))}
                                            maxDate={
                                                customStartDate
                                                    ? new Date(
                                                        Math.min(
                                                            new Date(customStartDate).setDate(customStartDate.getDate() + 89),
                                                            new Date()
                                                        )
                                                    )
                                                    : new Date()
                                            }
                                            disabled={!customStartDate}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="End date"
                                            className="form-control form-control-sm reelo-date-input"
                                            wrapperClassName="date-picker-wrapper"
                                        />
                                    </div>

                                    <Button
                                        size="sm"
                                        onClick={handleApplyCustomRange}
                                        disabled={!customStartDate || !customEndDate}
                                        style={{
                                            height: 36,
                                            borderRadius: 10,
                                            background: "#101820",
                                            borderColor: "#101820",
                                            fontWeight: 800,
                                            paddingInline: 16,
                                        }}
                                    >
                                        Apply
                                    </Button>
                                </div>
                            )}

                            {dateFilter === "custom" && customDateError && (
                                <span style={{ fontSize: 11, color: "#dc2626", width: "100%", paddingLeft: 48 }}>
                                    ⚠ {customDateError}
                                </span>
                            )}


                        </div>

                    </div>
                </div>

                {loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 340, gap: 12 }}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading outlet details...</span>
                    </div>
                )}
                {error && (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: 36, color: '#ef4444', display: 'block', marginBottom: 10 }} />
                        <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>{error}</div>
                        <button onClick={fetchDetails} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#4d8af0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
                                <div>
                                    <div style={{ fontSize: 28, lineHeight: 1.12, fontWeight: 900, color: '#102022' }}>
                                        {activeOutletName}, business at a glance ✨
                                    </div>
                                </div>
                                <button type="button" onClick={fetchDetails} style={{ border: '1px solid #d5e1e3', background: '#fff', borderRadius: 11, padding: '10px 14px', fontWeight: 900, color: '#102022', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <i className="bi bi-arrow-clockwise" style={{ color: '#10b981' }} /> Refresh report
                                </button>
                            </div>

                            <ReeloHeroBanner
                                outletName={activeOutletName}
                                dateLabel={fmtDateLabel(dateFilter, appliedCustomRange)}
                                bestInsight={bestInsight}
                                riskInsight={riskInsight}
                                periodCopy={periodCopy}
                            />

                            <ReeloSectionTitle icon="bi-stars" title={periodCopy.highlightsTitle} />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 14, marginBottom: 22 }}>
                                <ReeloHighlightCard title="Net Sales" value={compactCurrency(netSalesNum)} subtitle={`${fmtPct(revenueHealthPct)} of total sales`} icon="bi-currency-rupee" tone="orange" tooltip="This does not include Zomato & Swiggy sales" />
                                <ReeloHighlightCard title="Orders" value={fmtNum(totalOrdersNum)} subtitle={`${fmtNum(netOrders)} net · ${fmtPct(cancellationRate)} cancelled`} icon="bi-bag-fill" tone="pink" onClick={() => openOrdersModal('total')} />
                                <ReeloHighlightCard title="AOV" value={compactCurrency(avgOrderValue)} subtitle="Average order value" icon="bi-graph-up-arrow" tone="blue" />
                                <ReeloHighlightCard title="Alerts" value={fmtNum(issueTotal)} subtitle={issueTotal ? 'Hover Alerts tab for breakdown' : 'All clear'} icon="bi-bell-fill" tone={issueTotal ? 'red' : 'purple'} />
                            </div>

                            <ReeloSectionTitle icon="bi-lightning-charge" title="Business Highlights" />
                            <ReeloShellCard style={{ padding: '18px 20px', marginBottom: 18 }}>
                                <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', flexWrap: 'wrap', borderBottom: '1px solid #dfe7e8', marginBottom: 16 }}>
                                    {businessMetrics.map((m) => <ReeloBusinessMetric key={m.label} {...m} />)}
                                    <ReeloAlertMetric value={fmtNum(issueTotal)} sub={issueTotal ? 'Hover to view values' : 'All clear'} items={alertBreakdownItems} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 10 }}>
                                    <ReeloInsightPill icon="bi-clock-history" label="Delayed Orders" value={`${fmtNum(delayedNum)} (${fmtPct(delayedRate)})`} tone={delayedRate > 10 ? 'red' : 'amber'} />
                                    <ReeloInsightPill icon="bi-lightning-fill" label="Early Orders" value={`${fmtNum(earlyNum)} (${fmtPct(earlyRate)})`} tone="green" />
                                    <ReeloInsightPill icon="bi-percent" label="Discount Burn" value={`${fmt(discountNum, 0)} (${fmtPct(discountRate)})`} tone={discountRate > 15 ? 'amber' : 'blue'} />
                                    <ReeloInsightPill icon="bi-phone-fill" label="Online Share" value={`${fmtPct(onlineContribution)} of sales`} tone="purple" />
                                </div>
                            </ReeloShellCard>

                            <div className="revenue-order-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr .8fr', gap: 12 }}>
                                <RevenueChannelHealthCard
                                    netSalesNum={netSalesNum}
                                    zomatoRevenueNum={zomatoRevenueNum}
                                    swiggyRevenueNum={swiggyRevenueNum}
                                    netSalesWithAggregators={netSalesWithAggregators}
                                    totalSalesNum={totalSalesNum}
                                    discountNum={discountNum}
                                    complimentaryAmount={Number(stats?.complimentaryOrderAmount) || 0}
                                    revenueHealthPct={revenueHealthPct}
                                    onlineRevenueNum={onlineRevenueNum}
                                    onlineContribution={onlineContribution}
                                    avgOrderValue={avgOrderValue}
                                    channelData={channelData}
                                    banner={revenueBanner}
                                />
                                <OrderHealthControlCard
                                    totalOrders={totalOrdersNum}
                                    netOrders={netOrders}
                                    earlyOrders={earlyNum}
                                    onTimeOrders={onTimeNum}
                                    delayedOrders={delayedNum}
                                    cancelledOrders={cancelledNum}
                                    complimentaryOrders={complimentaryNum}
                                    orderHealthPct={orderHealthPct}
                                    cancellationRate={cancellationRate}
                                    delayedRate={delayedRate}
                                    earlyRate={earlyRate}
                                    banner={orderBanner}
                                    onOpenOrders={openOrdersModal}
                                />
                            </div>

                            {/* ── Offline Order + QR Web Orders performance ── */}
                            <div style={{ marginTop: 12 }}>
                                <OrderChannelsAccordionCard
                                    quickServeInfo={quickServeInfo}
                                    quickOrderInfo={quickOrderInfo}
                                    appRevenue={appOnlyNetSale}
                                    kioskRevenue={kioskNetSaleNum}
                                />
                                <ComplimentaryOrdersCard
                                    complimentaryOrders={complimentaryNum}
                                    complimentaryTaxable={stats?.complimentaryOrdersTaxable || stats?.complementaryOrdersTaxableAmount}
                                    complimentaryAmount={stats?.complimentaryOrdersTaxable || stats?.complementaryOrdersTaxableAmount}
                                // complimentaryAmount={stats?.complimentaryOrderAmount}

                                />
                            </div>
                        </div>

                        {/* ── Trend Row ── */}
                        {isTableVisible && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 10 }}>
                                <Card title="Sales & Orders Through The Day" icon="bi-bar-chart-line-fill">
                                    <HourlyComboChart hourlyDistribution={hourlyDistribution} />
                                </Card>
                            </div>
                        )}

                        {/* ── Product + Coupon Cards ── */}
                        <div className="bento-grid mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.15fr', gap: 12, alignItems: 'stretch' }}>
                            <CouponPerformanceCard coupons={topCoupons} />
                            <ProductPerformanceCard title="Top Performers" subtitle="Products creating maximum movement" products={topProducts} isTop={true} />
                            <ProductPerformanceCard title="Low Performers" subtitle="Products that may need attention" products={bottomProducts} isTop={false} />
                        </div>

                        {/* ── Operational Alerts ── */}
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', margin: '18px 0 8px 2px' }}>
                            Operational Alerts
                        </div>
                        <div className="mt-0" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
                            <BottomStatCard
                                id={ALERT_CARD_IDS.stockOut}
                                highlighted={highlightedAlert === 'stockOut'}
                                icon="bi-exclamation-triangle-fill"
                                title="Stock Out Alert"
                                value={fmtNum(exhaustedStockSummary?.totalExhaustedEntries)}
                                valueLabel="Products Exhausted"
                                severity={exhaustedStockSummary?.totalExhaustedEntries > 0 ? 'danger' : 'ok'}
                                products={exhaustedStockSummary?.categories}
                                footerNote="Count reflects real-time stock-out status as of today, regardless of the selected date filter."
                            />
                            {/* Commented Outlet Closures  */}
                            {/* <BottomStatCard
                                id={ALERT_CARD_IDS.closures}
                                highlighted={highlightedAlert === 'closures'}
                                icon="bi-door-closed-fill"
                                title="Outlet Closures"
                                value={fmtNum(outletShiftSummary?.outletClosedTotalTimes)}
                                valueLabel={outletShiftSummary?.outletClosedTotalTimes === 1 ? periodCopy.closureLabelSingular : periodCopy.closureLabelPlural}
                                rows={[{ label: 'Total Duration', value: formatMinutes(outletShiftSummary?.outletClosedTotalDurationInMinutes) }]}
                                severity={outletShiftSummary?.outletClosedTotalTimes > 0 ? 'warning' : 'ok'}
                            /> */}

                            <BottomStatCard
                                id={ALERT_CARD_IDS.damage}
                                highlighted={highlightedAlert === 'damage'}
                                icon="bi-box-seam"
                                title="Damage Summary"
                                value={fmtNum(damagedProductsNum + damagedRawMaterialNum)}
                                valueLabel="Total Damaged (Products + Raw Materials)"
                                rows={[
                                    { label: 'Damaged Products', value: fmtNum(damagedProductsNum) },
                                    { label: 'Damaged Raw Materials', value: fmtNum(damagedRawMaterialNum) },
                                ]}
                                severity={(damagedProductsNum + damagedRawMaterialNum) > 0 ? 'danger' : 'ok'}
                                products={damagedProductsSummary?.products}
                                rawMaterials={rawMaterialSummary?.rawMaterials}
                            />
                            <BottomStatCard
                                icon="bi-cash-stack"
                                title="Petty Cash Summary"
                                value={fmt(pettyCashSummary?.netExpense)}
                                valueLabel={`${pettyCashSummary?.totalCreditAmount > 0 ? '1+' : '0'} Credit Transaction`}
                                rows={[
                                    { label: 'Credit Amount', value: fmt(pettyCashSummary?.totalCreditAmount), color: '#16a34a' },
                                    { label: 'Debit Amount', value: fmt(pettyCashSummary?.totalDebitAmount), color: '#dc2626' },
                                    // { label: 'Net Balance', value: fmt((pettyCashSummary?.netExpense) || 0), tooltip: 'This balance reflects the amount currently available as of today, not the selected date range.', },
                                ]}
                                footerNote="Net Balance reflects today's running balance; Credit/Debit reflect the selected date range."
                                severity="ok"
                                toolTip="This balance reflects the amount currently available as of today, not the selected date range."
                            />
                        </div>
                    </>
                )}

                <style>{`
                    @media (max-width: 1200px) {
                        #outlet-detail-root [style*="repeat(4,minmax"] {
                            grid-template-columns: repeat(2,minmax(0,1fr)) !important;
                        }
                    }

                    @media (max-width: 960px) {
                        .revenue-order-grid {
                            grid-template-columns: 1fr !important;
                        }

                        .bento-grid {
                            grid-template-columns: 1fr 1fr !important;
                        }

                        #outlet-detail-root [style*="repeat(3,minmax"] {
                            grid-template-columns: 1fr !important;
                        }
                    }

                    @media (max-width: 620px) {
                        .bento-grid {
                            grid-template-columns: 1fr !important;
                        }

                        #outlet-detail-root [style*="repeat(4,minmax"],
                        #outlet-detail-root [style*="repeat(2,minmax"] {
                            grid-template-columns: 1fr !important;
                        }
                    }

                    /* ==========================================
                    DATE PICKER Z-INDEX FIX
                    ========================================== */

                    .date-picker-wrapper {
                        position: relative;
                        z-index: 100000 !important;
                    }

                    .date-picker-wrapper .react-datepicker-popper {
                        z-index: 100000 !important;
                    }

                    .react-datepicker-popper {
                        z-index: 100000 !important;
                    }

                    .react-datepicker {
                        z-index: 100001 !important;
                    }

                    /* Lighter highlight for selected / in-range dates */

                    .date-picker-wrapper .react-datepicker__day--selected,
                    .date-picker-wrapper .react-datepicker__day--range-start,
                    .date-picker-wrapper .react-datepicker__day--range-end,
                    .date-picker-wrapper .react-datepicker__day--keyboard-selected {
                        background-color: #cfe3fb !important;
                        color: #102022 !important;
                        font-weight: 700;
                        border-radius: 6px;
                    }

                    .date-picker-wrapper .react-datepicker__day--in-range,
                    .date-picker-wrapper .react-datepicker__day--in-selecting-range {
                        background-color: #eaf3fd !important;
                        color: #102022 !important;
                        border-radius: 6px;
                    }

                    .date-picker-wrapper .react-datepicker__day--selected:hover,
                    .date-picker-wrapper .react-datepicker__day--in-range:hover,
                    .date-picker-wrapper .react-datepicker__day--in-selecting-range:hover {
                        background-color: #bcd8f7 !important;
                    }

                `}</style>


            </div>

            <OrdersModal
                show={ordersModal.show}
                onHide={closeOrdersModal}
                modalType={ordersModal.type}
                outletId={activeOutletId}
                dateFilter={dateFilter}
                appliedCustomRange={appliedCustomRange}
                outletName={activeOutletName}
            />
        </React.Fragment>
    );
};

export default OutletDetailScreen;